export interface Component {
  kind: Kind;
  data?: unknown;
  formula?: unknown;
  canHaveChildren: boolean;
  permittedChildrenKind: Kind[];
  cleanTime: number;
  basePrice: number;
}

export enum Kind {
  AccessPanel = "AccessPanel",
  AirHandlingUnit = "AirHandlingUnit",
  AirHandlingUnitSchwerpunkt = "AirHandlingUnitSchwerpunkt",
  AirHandlingUnitSystem = "AirHandlingUnitSystem",
  AwayAirDuctAngular = "AwayAirDuctAngular",
  AwayAirDuctRound = "AwayAirDuctRound",
  CoolingCoil = "CoolingCoil",
  CrossFlowHeatExchanger = "CrossFlowHeatExchanger",
  DropletEliminator = "DropletEliminator",
  ExhaustDuctAngular = "ExhaustDuctAngular",
  ExhaustDuctRound = "ExhaustDuctRound",
  ExtraHours = "ExtraHours",
  Fan = "Fan",
  FilterSection = "FilterSection",
  FireDamper = "FireDamper",
  FreeText = "FreeText",
  HeatingCoil = "HeatingCoil",
  OutdoorAirDamper = "OutdoorAirDamper",
  OutdoorAirDuctAngular = "OutdoorAirDuctAngular",
  OutdoorAirDuctRound = "OutdoorAirDuctRound",
  RecirculatingSprayHumidifier = "RecirculatingSprayHumidifier",
  RecirculationAirDamper = "RecirculationAirDamper",
  RotaryHeatExchanger = "RotaryHeatExchanger",
  SoundAttenuator = "SoundAttenuator",
  SplitUnit = "SplitUnit",
  SupplyDuctAngular = "SupplyDuctAngular",
  SupplyDuctRound = "SupplyDuctRound",
}

export interface Common<Data, Formula> {
  kind: Kind;
  data: Data;
  formula: Formula;
  canHaveChildren: boolean;
  permittedChildrenKind: Kind[];
  cleanTime: number;
  basePrice: number;
}

export interface LengthHeightData {
  cleaningKind: CleaningKind;
  length: number;
  height: number;
}

export interface LengthHeightCountData extends LengthHeightData {
  count: number;
}

export interface SquareMeterFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}

export type AccessPanel = Common<AccessPanelData, AccessPanelFormula>;

export interface AccessPanelData {
  count: number;
}

export interface AccessPanelFormula {
  pricePerPanel: number;
}

export enum CleaningKind {
  Service = "Service",
  Sanierung = "Sanierung",
  Schwerpunkt = "Schwerpunkt",
}

export type AirHandlingUnit = Common<
  AirHandlingUnitData,
  AirHandlingUnitFormula
>;

export interface AirHandlingUnitData {
  cleaningKind: CleaningKind;
  length: number;
  width: number;
  height: number;
}

export interface AirHandlingUnitFormula {
  sanierungFactor: number;
  minutesPerCubicMeter: number;
  hourlyRate: number;
}

export type AirHandlingUnitSchwerpunkt = Common<
  AirHandlingUnitSchwerpunktData,
  AirHandlingUnitSchwerpunktFormula
>;

export interface AirHandlingUnitSchwerpunktData {
  cleanTime: number;
}

export interface AirHandlingUnitSchwerpunktFormula {
  hourlyRate: number;
}

export interface AirHandlingUnitSystem extends Component {
  designation: string;
  cleaningKind: CleaningKind;
}

export type AwayAirDuctAngular = Common<
  AwayAirDuctAngularData,
  AwayAirDuctAngularFormula
>;

export interface AwayAirDuctAngularData {
  cleaningKind: CleaningKind;
  length: number;
  width: number;
  height: number;
}

export interface AwayAirDuctAngularFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}

export type AwayAirDuctRound = Common<
  AwayAirDuctRoundData,
  AwayAirDuctRoundFormula
>;

export interface AwayAirDuctRoundData {
  cleaningKind: CleaningKind;
  length: number;
  diameter: number;
}

export interface AwayAirDuctRoundFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}

export type CoolingCoil = Common<CoolingCoilData, CoolingCoilFormula>;

export type CoolingCoilData = LengthHeightData;

export type CoolingCoilFormula = SquareMeterFormula;

export type CrossFlowHeatExchanger = Common<
  CrossFlowHeatExchangerData,
  CrossFlowHeatExchangerFormula
>;

export interface CrossFlowHeatExchangerData {
  cleaningKind: CleaningKind;
  length: number;
  width: number;
}

export interface CrossFlowHeatExchangerFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}

export type DropletEliminator = Common<
  DropletEliminatorData,
  DropletEliminatorFormula
>;

export type DropletEliminatorData = LengthHeightCountData;

export type DropletEliminatorFormula = SquareMeterFormula;

export type ExhaustDuctAngular = Common<
  ExhaustDuctAngularData,
  ExhaustDuctAngularFormula
>;

export interface ExhaustDuctAngularData {
  cleaningKind: CleaningKind;
  length: number;
  width: number;
  height: number;
}

export interface ExhaustDuctAngularFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}

export type ExhaustDuctRound = Common<
  ExhaustDuctRoundData,
  ExhaustDuctRoundFormula
>;

export interface ExhaustDuctRoundData {
  cleaningKind: CleaningKind;
  length: number;
  diameter: number;
}

export interface ExhaustDuctRoundFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}

export type ExtraHours = Common<ExtraHoursData, ExtraHoursFormula>;

export interface ExtraHoursData {
  hours: number;
}

export interface ExtraHoursFormula {
  hourlyRate: number;
}

export type Fan = Common<FanData, FanFormula>;

export type FanData = LengthHeightCountData;

export type FanFormula = SquareMeterFormula;

export type FilterSection = Common<FilterSectionData, FilterSectionFormula>;

export type FilterSectionData = LengthHeightCountData;

export type FilterSectionFormula = SquareMeterFormula;

export type FireDamper = Common<FireDamperData, FireDamperFormula>;

export type FireDamperData = LengthHeightCountData;

export type FireDamperFormula = SquareMeterFormula;

export type FreeText = Common<FreeTextData, FreeTextFormula>;

export interface FreeTextData {
  text: string;
  price: number;
}

export type FreeTextFormula = object;

export type HeatingCoil = Common<HeatingCoilData, HeatingCoilFormula>;

export type HeatingCoilData = LengthHeightData;

export type HeatingCoilFormula = SquareMeterFormula;

export type OutdoorAirDamper = Common<
  OutdoorAirDamperData,
  OutdoorAirDamperFormula
>;

export type OutdoorAirDamperData = LengthHeightCountData;

export type OutdoorAirDamperFormula = SquareMeterFormula;

export type OutdoorAirDuctAngular = Common<
  OutdoorAirDuctAngularData,
  AwayAirDuctAngularFormula
>;

export interface OutdoorAirDuctAngularData {
  cleaningKind: CleaningKind;
  length: number;
  width: number;
  height: number;
}

export interface OutdoorAirDuctAngularFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}

export type OutdoorAirDuctRound = Common<
  OutdoorAirDuctRoundData,
  AwayAirDuctRoundFormula
>;

export interface OutdoorAirDuctRoundData {
  cleaningKind: CleaningKind;
  length: number;
  diameter: number;
}

export interface OutdoorAirDuctRoundFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}

export type RecirculatingSprayHumidifier = Common<
  RecirculatingSprayHumidifierData,
  RecirculatingSprayHumidifierFormula
>;

export interface RecirculatingSprayHumidifierData {
  cleaningKind: CleaningKind;
  floorLength: number;
  floorWidth: number;
  nozzleCount: number;
}

export interface RecirculatingSprayHumidifierFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  minutesPerNozzle: number;
  hourlyRate: number;
}

export type RecirculationAirDamper = Common<
  RecirculationAirDamperData,
  RecirculationAirDamperFormula
>;

export type RecirculationAirDamperData = LengthHeightCountData;

export type RecirculationAirDamperFormula = SquareMeterFormula;

export type RotaryHeatExchanger = Common<
  RotaryHeatExchangerData,
  RotaryHeatExchangerFormula
>;

export interface RotaryHeatExchangerData {
  cleaningKind: CleaningKind;
  diameter: number;
}

export interface RotaryHeatExchangerFormula {
  sanierungFactor: number;
  minutesPerDiameter: number;
  hourlyRate: number;
}

export type SoundAttenuator = Common<
  SoundAttenuatorData,
  SoundAttenuatorFormula
>;

export type SoundAttenuatorData = LengthHeightCountData;

export type SoundAttenuatorFormula = SquareMeterFormula;

export type SplitUnit = Common<SplitUnitData, SplitUnitFormula>;

export interface SplitUnitData {
  count: number;
}

export interface SplitUnitFormula {
  hoursPerUnit: number;
  hourlyRate: number;
}

export type SupplyDuctAngular = Common<
  SupplyDuctAngularData,
  AwayAirDuctAngularFormula
>;

export interface SupplyDuctAngularData {
  cleaningKind: CleaningKind;
  length: number;
  width: number;
  height: number;
}

export interface SupplyDuctAngularFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}

export type SupplyDuctRound = Common<
  SupplyDuctRoundData,
  AwayAirDuctRoundFormula
>;

export interface SupplyDuctRoundData {
  cleaningKind: CleaningKind;
  length: number;
  diameter: number;
}

export interface SupplyDuctRoundFormula {
  sanierungFactor: number;
  minutesPerSquareMeter: number;
  hourlyRate: number;
}
